import pluralize from 'pluralize';

import { NO_PERMISSION } from '@air/errors';
import { isAirrorInstance } from '@air/utils-error';

type UnknownRecord = Record<string, unknown>;

type NoPermissionExtra = {
  scope: string;
  property: string;
};

// Removing an asset from its last board implicitly moves it to the workspace root,
// which the backend gates behind this workspace-scoped permission.
const LAST_BOARD_REMOVAL_SCOPE = 'asset';
const LAST_BOARD_REMOVAL_PROPERTY = 'canCreate';

const isUnknownRecord = (value: unknown): value is UnknownRecord => typeof value === 'object' && value !== null;

const isNoPermissionExtra = (extra: unknown): extra is NoPermissionExtra =>
  isUnknownRecord(extra) && typeof extra.scope === 'string' && typeof extra.property === 'string';

const isLastBoardRemovalPermissionError = (error: unknown): boolean => {
  if (!isAirrorInstance(error) || error.type !== NO_PERMISSION.type) {
    return false;
  }

  return (
    isNoPermissionExtra(error.extra) &&
    error.extra.scope === LAST_BOARD_REMOVAL_SCOPE &&
    error.extra.property === LAST_BOARD_REMOVAL_PROPERTY
  );
};

export const getRemoveAssetsFromBoardErrorMessage = (
  error: unknown,
  assetCount: number,
  boardTitle: string,
): string => {
  if (isLastBoardRemovalPermissionError(error)) {
    return assetCount === 1
      ? 'Your role cannot remove this asset, since it only exists on one board. Ask an editor or admin to remove this for you.'
      : 'Your role cannot remove these assets, since some of them only exist on one board. Ask an editor or admin to remove them for you.';
  }

  return `There was an error removing ${pluralize('item', assetCount, true)} from ${boardTitle}. Please try again later.`;
};
